import * as React from "react";
import { Separator } from "@/components/ui/separator";
import {
  Clock,
  LoaderCircle,
  Pause,
  PhoneIncoming,
  PhoneOutgoing,
  Play,
} from "lucide-react";
import { CallHistory } from "@/interface/dataInterface";
import axiosInstance from "@/utils/axiosInstance";
import { convertSeconds } from "@/utils/generateTimeSlots";
import { Button } from "@/components/ui/button";
import { useSipContext } from "@/context/SipContext";

const CallsTimeline = ({ phoneNumber }: { phoneNumber: string }) => {
  const { recordingUrl: recording_url, apiKey: key } = useSipContext();
  const [callLogs, setCallLogs] = React.useState<CallHistory[]>([]);
  const [uniqueDates, setUniqueDates] = React.useState<string[]>([]);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleRecord = () => {
    if (!isPlaying) audioRef.current?.play();
    else audioRef.current?.pause();
    setIsPlaying((prev) => !prev);
  };

  React.useEffect(() => {
    if (!phoneNumber) return;
    setLoading(true);
    const getLogs = async () => {
      const { data } = await axiosInstance.get(
        `/call-logs/client/${phoneNumber}`,
      );
      setCallLogs(data);
      setLoading(false);
    };

    getLogs();
  }, [phoneNumber]);

  React.useEffect(() => {
    const dates = [
      ...new Set(callLogs.map((item) => item.calldate.split(" ")[0])),
    ].reverse();
    setUniqueDates(dates);
  }, [callLogs]);

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center gap-x-2">
          <LoaderCircle className="animate-spin w-4 h-4" /> Loading...
        </div>
      ) : uniqueDates.length > 0 ? (
        <section className="w-full mt-5 overflow-hidden">
          <div className="grid gap-y-4">
            {uniqueDates.map((item) => (
              <div key={item} className="w-full">
                <p className="text-foreground/25 font-medium">{item}</p>

                <div className="w-full max-h-[500px] overflow-auto">
                  <div className="relative">
                    <Separator
                      orientation="vertical"
                      className="bg-muted absolute left-4 top-4"
                    />
                    {callLogs
                      .filter(
                        (call) =>
                          item === call.calldate.split(" ")[0] &&
                          call.disposition !== "FAILED",
                      )
                      .map((entry) => (
                        <div
                          key={entry.id}
                          className="relative w-full mb-10 pl-8"
                        >
                          <div className="bg-foreground absolute left-0 p-1.5 top-3.5 flex size-8 items-center justify-center rounded-full">
                            {entry.dcontext === "from-internal" ? (
                              <PhoneOutgoing className="text-background" />
                            ) : (
                              <PhoneIncoming className="text-background" />
                            )}
                          </div>
                          <div className="flex justify-between">
                            <div className="flex flex-col items-start justify-start">
                              <h4 className="flex items-center gap-x-2 rounded-xl pt-3 text-lg   tracking-tight px-3">
                                {entry.dcontext === "from-internal"
                                  ? "Outgoing call"
                                  : "Incoming call"}
                              </h4>
                              <p className="text-muted-foreground ps-4">
                                <div className="flex gap-x-2 items-center">
                                  <Clock className="w-4 h-4" />{" "}
                                  {entry.calldate.split(" ")[1]}
                                </div>{" "}
                                {entry.disposition !== "FAILED" &&
                                  `| 
                          ${entry.disposition} ${entry.disposition === "ANSWERED" ? `, ${convertSeconds(entry.duration)} | Picked by ${entry.dcontext === "from-internal" ? entry.cnum : entry.dst}` : ""}`}
                              </p>
                            </div>

                            <>
                              {entry.disposition === "ANSWERED" && (
                                <div className="relative z-50 flex items-center gap-x-2 pe-5">
                                  <Button
                                    className="bg-gray-500 hover:bg-gray-600 rounded-full"
                                    onClick={handleRecord}
                                  >
                                    {isPlaying ? <Pause /> : <Play />}
                                  </Button>
                                </div>
                              )}

                              {entry.recordingfile && (
                                <audio
                                  ref={audioRef}
                                  src={`${recording_url}=${entry.recordingfile}&apikey=${key}`}
                                  onEnded={() => setIsPlaying(false)}
                                />
                              )}
                            </>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : <p className="text-center font-semibold">No logs</p>}
    </>
  );
};

export default CallsTimeline;
